import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, zip } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { ApiCategory, ApiCategoryList } from 'idt-ecommerce-api/_api/api-catalog/api-categories/api-categories.models';
import { ApiCategoriesService } from 'idt-ecommerce-api/_api/api-catalog/api-categories/api-categories.service';
import { ApiStoreXConf, ApiStoreXConfEnum } from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { ApiStoresService } from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.service';
import { ApiCmsConfiguration } from 'idt-ecommerce-api/_api/providers/api-cms/api-cms-configurations/api-cms-configurations.models';
import { ApiCmsConfigurationsService } from 'idt-ecommerce-api/_api/providers/api-cms/api-cms-configurations/api-cms-configurations.service';
import { ApiCmsTranslationsService } from 'idt-ecommerce-api/_api/providers/api-cms/api-cms-translations/api-cms-translations.service';
import { CategoriesHelpers } from 'idt-ecommerce-api/catalog/categories/categories.helpers';
import { Category } from 'idt-ecommerce-api/catalog/categories/categories.models';
import { CategoriesModelsTransformer } from 'idt-ecommerce-api/catalog/categories/categories.models.transformer';
import { EcommerceConfig } from 'idt-ecommerce-api/config/config.models';
import { EcommerceConfigService } from 'idt-ecommerce-api/config/ecommerce-config.service';
import { Seo } from 'idt-ecommerce-api/seo/seo.models';
import { SeoService } from 'idt-ecommerce-api/seo/seo.service';
import {
  StoreCatalog, StoreCmsConfigurationsObject, StoreDetail, StoreLanguage, StoreSummary, StoreXconfigurationsObject
} from 'idt-ecommerce-api/stores/stores.models';
import { StoresModelsTransformer } from 'idt-ecommerce-api/stores/stores.models.transformer';

import { EcommerceContext } from './context.models';


@Injectable()
export class ContextService {

  private readonly context$: BehaviorSubject<EcommerceContext>;

  constructor(
    private readonly apiCategoriesService: ApiCategoriesService,
    private readonly apiCmsConfigurationsService: ApiCmsConfigurationsService,
    private readonly apiCmsTranslationsService: ApiCmsTranslationsService,
    private readonly apiStoresService: ApiStoresService,
    private readonly ecommerceConfigService: EcommerceConfigService,
    private readonly seoService: SeoService
  ) {
    this.context$ = new BehaviorSubject<EcommerceContext>({
      allStores: [],
      categories: [],
      config: {} as EcommerceConfig,
      currentCatalogId: '',
      currentLanguage: {} as StoreLanguage, // TODO: replace currentLanguage by currentLanguageId
      seo: {} as Seo,
      store: {} as StoreDetail
    });
  }

  public getContext$(): Observable<EcommerceContext> {
    return this.context$;
  }

  public getAllStores$(): Observable<StoreSummary[]> {
    return this.context$.pipe(map((context) => context.allStores));
  }

  public getCategories$(): Observable<Category[]> {
    return this.context$.pipe(map((context) => context.categories));
  }

  public getCurrentLanguage$(): Observable<StoreLanguage> {
    return this.context$.pipe(map((context) => context.currentLanguage));
  }

  public getStore$(): Observable<StoreDetail> {
    return this.context$.pipe(map((context) => context.store));
  }

  public setAllStores(allStores: StoreSummary[]): void {
    this.context$.next({
      ...this.context$.getValue(),
      allStores
    });
  }

  public setStoreTimestamp(timestampBuildVersion: string): void {
    const store = this.context$.getValue().store;
    this.context$.next({
      ...this.context$.getValue(),
      store: {
        ...store,
        details: {
          ...store.details,
          timestampBuildVersion
        }
      }
    });
  }

  public init(countryCode: string, languageCode: string): void {
    this.apiStoresService.retrieveDefaultStoreIdAndLanguageId(countryCode, languageCode).pipe(
      take(1),
      filter((result): result is { storeId: string; languageId: string } => (
        result.storeId !== undefined
      )),
      switchMap((result) => this.createContext$(result))
    ).subscribe((context) => {
      this.context$.next(context);
    });
  }

  public initWithStoreAndLanguage(params: { storeId: string; languageId: string }): void {
    this.createContext$(params).subscribe((context) => {
      this.context$.next(context);
    });
  }

  private createContext$(params: { storeId: string; languageId: string }): Observable<EcommerceContext> {
    const config = this.ecommerceConfigService.getConfig();
    return zip(
      this.apiStoresService.storeDetail(params.storeId, params.languageId),
      this.seoService.seo()
    ).pipe(
      map(([store, seo]) => ({
        seo,
        store: StoresModelsTransformer.storeDetailFromApi(store, config.appId)
      })),
      switchMap(({ seo, store }) => {
        const currentLanguage = store.supportedLanguages.find((language) => language.id === params.languageId)!;

        return combineLatest([
          this.getCMSConfigurations(store.countryCode),
          this.apiCmsTranslationsService.translations(store.countryCode, currentLanguage.code),
        ]).pipe(
          map(([cmsConfigurations, apiTranslations]) => ({
            ...this.context$.getValue(),
            config,
            currentCatalogId: store.catalogs.find(
              (catalog: StoreCatalog) => catalog.type === config.catalogType
            )!.id,
            currentLanguage,
            seo,
            store: {
              ...store,
              details: {
                ...store.details,
                translations: apiTranslations,
                cmsConfigurations
              }
            }
          })),
          switchMap((context) => combineLatest([
            this.getCategories(context),
            this.getXConfigurations(context.store, context.currentLanguage)
          ]).pipe(
            map(([categories, xConfigurations]) => ({
              ...context,
              categories,
              store: {
                ...context.store,
                details: {
                  ...context.store.details,
                  xConfigurations
                }
              }
            }))
          ))
        );
      }),
      take(1)
    );
  }

  private getCMSConfigurations(countryCode: string): Observable<StoreCmsConfigurationsObject> {
    return this.apiCmsConfigurationsService.configurations(
      countryCode,
      'webApp'
    ).pipe(
      map((configurations: ApiCmsConfiguration[]) => StoresModelsTransformer.cmsConfigurationsFromProviderApi(
        configurations,
        this.ecommerceConfigService.getConfig().appId
      ))
    );
  }

  private getCategories(context: EcommerceContext): Observable<Category[]> {
    return this.apiCategoriesService.categories(
      context.store.id,
      context.currentCatalogId,
      context.currentLanguage.id
    ).pipe(
      map((apiCategoryList: ApiCategoryList) => {
        const categories = apiCategoryList.categories.map(
          (apiCategory: ApiCategory) => CategoriesModelsTransformer.categoryFromApi(
            apiCategory,
            context
          )
        );
        return CategoriesHelpers.filterCategories(categories, this.ecommerceConfigService.getConfig().appId);
      }),
      catchError((error) => {
        console.error('Error retrieving categories:', error);
        return of([]);
      })
    );
  }

  private getXConfigurations(store: StoreDetail, language: StoreLanguage): Observable<StoreXconfigurationsObject> {
    return this.apiStoresService.storeXConf(
      store.id,
      language.id,
      [ApiStoreXConfEnum.SEQUENCE_FROM_STOCK_ENABLED, ApiStoreXConfEnum.LAST_SIZES_ENABLED]
    ).pipe(
      map((apiXConf: ApiStoreXConf) => StoresModelsTransformer.xConfigurationsFromApi(apiXConf))
    );
  }

}
